import {
  Box, Container, Typography, Button, Grid,
  Card, CardContent, Stack, Chip, Divider, alpha,
} from '@mui/material'
import {
  IconDroplet, IconClipboardList, IconChartAreaLine,
  IconMap2, IconBell, IconSeeding, IconArrowRight,
  IconUsers, IconReportAnalytics, IconBuildingBank,
} from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { tokens } from '@/theme'
import DataDashboard from '@/components/DataDashboard'

// ─── Feature cards ────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: <IconClipboardList size={26} stroke={1.5} />, title: 'Meeting Summaries',      body: 'GSA agenda items, decisions, and key updates organized so stakeholders don\'t have to parse dense agency documents.'  },
  { icon: <IconDroplet       size={26} stroke={1.5} />, title: 'Monitoring Data',         body: 'Basin-level monitoring from CASGEM and local agencies, aggregated into readable summaries with trend visibility.'       },
  { icon: <IconChartAreaLine size={26} stroke={1.5} />, title: 'Reporting Visibility',    body: 'Water-use and field data translated into structured reports — improving clarity for growers, consultants, and agencies.' },
  { icon: <IconMap2          size={26} stroke={1.5} />, title: 'Basin Awareness',         body: 'Track what\'s happening in your specific basin without losing regional context across California\'s SGMA landscape.'    },
  { icon: <IconSeeding        size={26} stroke={1.5} />, title: 'Irrigation & Field Data', body: 'ET estimates, crop water demand, and soil conditions alongside groundwater data — one complete picture.'                 },
  { icon: <IconBell          size={26} stroke={1.5} />, title: 'Timely Updates',          body: 'Policy changes, allocation updates, and agency announcements surfaced as they happen — plan ahead, not after.'          },
]

const AUDIENCES = [
  { icon: <IconSeeding          size={20} stroke={1.5} />, role: 'Growers & Farm Operators', message: 'Plain-language summaries of groundwater conditions and GSA announcements relevant to your region.' },
  { icon: <IconBuildingBank    size={20} stroke={1.5} />, role: 'GSAs & Water Agencies',    message: 'Organize the data you\'re already collecting — make it easier to share with stakeholders and oversight bodies.' },
  { icon: <IconReportAnalytics size={20} stroke={1.5} />, role: 'Agricultural Consultants', message: 'Less time searching for information, more time applying it to client reporting and planning.' },
  { icon: <IconUsers           size={20} stroke={1.5} />, role: 'Irrigation Districts',     message: 'Water delivery, efficiency, and usage data organized to support the reporting side of district operations.' },
]

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function GsaCalifornia() {
  return (
    <>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${tokens.blue.dark} 0%, ${tokens.green.dark} 100%)`,
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 7, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `radial-gradient(${alpha('#fff', 0.04)} 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Chip
            label="California GSA Resources"
            size="small"
            sx={{
              fontFamily: '"Poppins", system-ui, sans-serif',
              fontSize: '0.68rem',
              letterSpacing: '0.12em',
              background: alpha('#fff', 0.1),
              color: 'rgba(255,255,255,0.75)',
              border: `1px solid ${alpha('#fff', 0.2)}`,
              mb: 3,
            }}
          />
          <Typography variant="h1" sx={{ color: '#fff', fontSize: { xs: '2.6rem', md: '4rem' }, mb: 2.5 }}>
            Clearer Data for<br />Groundwater Management
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 560, mx: 'auto', mb: 4 }}>
            AgroLense helps California GSAs, growers, and water stakeholders stay informed with organized groundwater data, reporting summaries, and SGMA updates.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<IconArrowRight size={18} />}
              sx={{ background: '#fff !important', color: `${tokens.blue.dark} !important` }}
              href="#how-it-helps"
            >
              How It Helps
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/news"
              sx={{
                borderColor: '#fff !important',
                borderWidth: '2px !important',
                color: '#fff !important',
                '&:hover': { background: alpha('#fff', 0.1) },
              }}
            >
              Browse Updates
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Challenge intro */}
      <Container maxWidth="md" sx={{ py: { xs: 7, md: 10 }, textAlign: 'center' }}>
        <Typography variant="overline" sx={{ color: tokens.blue.main, display: 'block', mb: 1 }}>
          The Challenge
        </Typography>
        <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
          Important Water Information Is Scattered Across Too Many Sources
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640, mx: 'auto' }}>
          GSA meetings, DWR reports, monitoring data, public notices, and agency updates live across dozens of systems. AgroLense brings it together — organized, readable, and accessible for everyone in the agricultural ecosystem.
        </Typography>
      </Container>

      <Divider sx={{ mx: { xs: 2, md: 8 } }} />

      {/* How it helps */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }} id="how-it-helps">
        <Box sx={{ mb: 6 }}>
          <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
            What AgroLense Does
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            Organized Data. Clearer Picture.
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {FEATURES.map(({ icon, title, body }) => (
            <Grid key={title} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex', p: 1.25, borderRadius: 1.5,
                      background: alpha(tokens.green.main, 0.08),
                      color: tokens.green.main, mb: 2,
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{body}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* SGMA explainer */}
      <Box sx={{ background: tokens.green.dark, color: '#fff', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 5, md: 8 },
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: alpha('#fff', 0.45), display: 'block', mb: 1 }}>
                Background
              </Typography>
              <Typography variant="h2" sx={{ color: '#fff', mb: 2.5, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                Understanding SGMA in California
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, mb: 1.5 }}>
                The Sustainable Groundwater Management Act (SGMA), signed in 2014, requires high- and medium-priority basins to achieve groundwater sustainability by 2040–2042.
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, mb: 3 }}>
                GSAs — Groundwater Sustainability Agencies — are the local bodies responsible for developing and implementing Groundwater Sustainability Plans (GSPs) for their basins.
              </Typography>
              <Box
                sx={{
                  borderLeft: `3px solid ${tokens.green[300]}`,
                  pl: 2, py: 1,
                  background: alpha('#fff', 0.05),
                  borderRadius: '0 4px 4px 0',
                }}
              >
                <Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>
                  AgroLense does not provide legal, engineering, or regulatory advice. Always confirm compliance details with your GSA or a qualified professional.
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 2, opacity: 0.9 }}>
                What GSAs are responsible for:
              </Typography>
              <Stack spacing={1.5} divider={<Divider sx={{ borderColor: alpha('#fff', 0.1) }} />}>
                {[
                  'Developing and updating Groundwater Sustainability Plans',
                  'Monitoring groundwater levels, quality, and extraction',
                  'Reporting to the California Department of Water Resources',
                  'Communicating with growers, landowners, and the public',
                  'Setting sustainable management criteria for their basin',
                  'Tracking progress toward sustainability goals',
                ].map(item => (
                  <Stack key={item} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ color: tokens.green[300], mt: '3px', flexShrink: 0 }}>→</Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Basin table (live API) */}
      <DataDashboard />

      {/* Audiences */}
      <Box sx={{ background: tokens.green['50'], py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 5 }}>
            <Typography variant="overline" sx={{ color: tokens.green.main, display: 'block', mb: 1 }}>
              Who AgroLense Supports
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
              Built for the Agricultural Ecosystem
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {AUDIENCES.map(({ icon, role, message }) => (
              <Grid key={role} size={{ xs: 12, sm: 6 }}>
                <Card sx={{ background: '#fff', height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ color: tokens.blue.main }}>{icon}</Box>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: '"Poppins", system-ui, sans-serif', color: tokens.blue.main, textTransform: 'uppercase', letterSpacing: '0.08em' }}
                      >
                        {role}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ background: tokens.blue.dark, color: '#fff', py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ color: '#fff', mb: 1.5, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Stay Informed on<br />California Groundwater
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mb: 4 }}>
            Monthly updates on GSA activity, SGMA developments, and water data aggregated for your region.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/news"
              endIcon={<IconArrowRight size={18} />}
              sx={{ background: '#fff !important', color: `${tokens.blue.dark} !important` }}
            >
              Get Updates
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={Link}
              to="/news"
              sx={{
                borderColor: alpha('#fff', 0.4), color: '#fff',
                '&:hover': { borderColor: '#fff', background: alpha('#fff', 0.06) },
              }}
            >
              Browse News
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  )
}
